import { alpha, darken, lighten, type Theme } from '@mui/material/styles';
import { getCellColor } from './cellColors';

// A bit more saturated than the shared Predictions/BoardScan red→amber→green scale, for the few spots on this
// page that render it at full strength (the row's position rail, the Avg Fin tile, the active sort column) —
// kept apart from cellColors.ts so it doesn't ripple into those other tables. Shared by the Simulator's tables,
// charts and driver panel.
export const vivid = (hex: string, boost = 0.22): string => {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  const s2 = Math.min(1, s + (1 - s) * boost);
  const hue2rgb = (p: number, q: number, t: number) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s2) : l + s2 - l * s2;
  const p = 2 * l - q;
  const toHex = (v: number) =>
    Math.round(v * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(hue2rgb(p, q, h + 1 / 3))}${toHex(hue2rgb(p, q, h))}${toHex(hue2rgb(p, q, h - 1 / 3))}`;
};

/** The vivid scale darkened a bit more in light mode, where the raw hue (especially the yellow midpoint)
 * doesn't have enough contrast against a white page on its own. */
export const strongCellColor = (theme: Theme, hex: string): string =>
  theme.palette.mode === 'light' ? darken(hex, 0.18) : hex;

/** The results table's body color (a touch lighter than the page surface in dark mode). */
export const tableBgFor = (theme: Theme): string =>
  theme.palette.mode === 'dark' ? lighten(theme.palette.background.paper, 0.05) : theme.palette.background.paper;

/**
 * Driver rating (0-150) placed on the 1st-40th position scale, so it can share the table's red-yellow-green colors:
 * 130 or better reads like 1st (green), 40 or worse like 40th (red). Fixed ends, so a number means the same color for
 * every driver.
 */
export const ratingAsPosition = (rating: number): number => 1 + (39 * (130 - Math.min(130, Math.max(40, rating)))) / 90;

// The same red→amber→green scale the table always had, full-strength on the active sort column and dimmed
// (not gray) everywhere else, so the field's shape still reads at a glance without drowning out the sort key.
// Shared by ProbCell and the mobile card's metric tiles so both read the same gradient the same way.
export const probCellColor = (theme: Theme, value: number, strong?: boolean): string => {
  const gradColor = strong ? vivid(getCellColor(value, 'probability')) : getCellColor(value, 'probability');
  // The scale's pastel colors (especially the yellow midpoint) read fine on a near-black panel but wash out
  // on white, so light mode gets a darkened variant of the same hue rather than the raw scale color — the
  // active sort column gets darkened further still, to keep it reading as the "pop" column against white.
  const modeColor = theme.palette.mode === 'light' ? darken(gradColor, strong ? 0.34 : 0.22) : gradColor;
  // Light mode also needs a higher alpha floor than dark mode for the dimmed (non-sort-key) columns.
  return strong ? modeColor : alpha(modeColor, theme.palette.mode === 'dark' ? 0.55 : 0.8);
};

/** A tinted (not solid-fill) version of the red/yellow/green position scale, legible on a dark panel. */
export const positionCellSx = (value: number, mode: 'light' | 'dark') => ({
  backgroundColor: alpha(vivid(getCellColor(value, 'position')), mode === 'dark' ? 0.58 : 0.85),
});
